import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stateBadge = (s) => s === 'SUPPRESS'
  ? 'bg-red-500/20 text-red-300 border border-red-500/50'
  : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50';

const delta = (b, w) => {
  if (b == null || w == null) return null;
  return w - b;
};

export default function SignalEffects() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [gate, setGate] = useState({ min_sample: '', hit_margin: '' });

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/quickpick/signal-effects/summary`, { credentials: 'include' });
    if (r.ok) {
      const d = await r.json();
      setData(d);
      setGate({ min_sample: d.gate.min_sample, hit_margin: d.gate.hit_margin });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runLearner = async () => {
    setRunning(true); setMsg('Learner started — recomputing across all data (~1–2 min)…');
    const r = await fetch(`${API_URL}/api/quickpick/signal-effects/run`, { method: 'POST', credentials: 'include' });
    const d = await r.json();
    if (!d.success) { setMsg(d.message || 'Could not start'); setRunning(false); return; }
    // poll every 15s for a fresh last_run
    const prev = data?.last_run;
    const iv = setInterval(async () => {
      const rr = await fetch(`${API_URL}/api/quickpick/signal-effects/summary`, { credentials: 'include' });
      if (rr.ok) {
        const dd = await rr.json();
        if (dd.last_run && dd.last_run !== prev) {
          setData(dd); setRunning(false); setMsg('✓ Learner finished — policy updated.');
          clearInterval(iv);
        }
      }
    }, 15000);
  };

  const setMode = async (id, mode) => {
    const r = await fetch(`${API_URL}/api/quickpick/signal-effects/policy`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ id, mode }),
    });
    if (r.ok) load();
  };

  const saveGate = async () => {
    await fetch(`${API_URL}/api/quickpick/signal-effects/gate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ min_sample: Number(gate.min_sample), hit_margin: Number(gate.hit_margin) }),
    });
    setMsg('Gate saved — applies on next learner run.');
    load();
  };

  if (!data) return <div className="text-slate-400" data-testid="signal-effects-loading">Loading Signal Effects…</div>;

  const rules = ['A', 'B', 'C', 'D', 'X'];

  return (
    <div className="space-y-6" data-testid="signal-effects-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Signal Effects</h2>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Marginal effect of the late 1MTP signals (Strong = must_use, Fade = dont_use) on the $100 Exacta,
            measured on the races each signal actually changes. Auto‑flip is gated on <strong>hit rate</strong>.
            Last run: <span className="text-slate-300">{data.last_run ? new Date(data.last_run).toLocaleString() : '—'}</span>
          </p>
        </div>
        <button onClick={runLearner} disabled={running} data-testid="signal-effects-run"
          className="px-4 py-2 rounded-lg font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white">
          {running ? 'Running…' : 'Run learner now'}
        </button>
      </div>

      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 space-y-3" data-testid="signal-effects-legend">
        <h3 className="text-sm font-bold text-white">What these toggles change</h3>
        <p className="text-xs text-slate-400 max-w-3xl">
          Each row controls whether the live ticket engine <strong>acts on</strong> a late 1‑MTP signal.
          Turning one <span className="text-red-300">OFF (SUPPRESS)</span> changes which horses get keyed and
          spread in real tickets. The Generated Tix backtest honors these same settings, so the report
          matches what the app actually plays.
        </p>
        <ul className="grid sm:grid-cols-3 gap-3 text-xs">
          <li className="bg-slate-800/60 border border-slate-700 rounded p-3">
            <span className="font-semibold text-emerald-300">Strong</span>
            <span className="text-slate-500"> (must_use)</span>
            <p className="text-slate-400 mt-1">Force‑includes a runner the late money strongly backs — locked into your keys.</p>
          </li>
          <li className="bg-slate-800/60 border border-slate-700 rounded p-3">
            <span className="font-semibold text-emerald-300">Fade</span>
            <span className="text-slate-500"> (dont_use)</span>
            <p className="text-slate-400 mt-1">Excludes a runner the late money abandons — floored to 3rd, so it can never sit in an exacta top‑2.</p>
          </li>
          <li className="bg-slate-800/60 border border-slate-700 rounded p-3">
            <span className="font-semibold text-emerald-300">Overbet</span>
            <span className="text-slate-500"> (demote fav)</span>
            <p className="text-slate-400 mt-1">Demotes an over‑bet favorite (model &amp; P3 pool price it far longer than the 1‑MTP market) out of the key slot.</p>
          </li>
        </ul>
        <p className="text-xs text-slate-500 max-w-3xl">
          <strong className="text-slate-400">Effective</strong> = what's live now (<span className="text-emerald-300">APPLY</span> = on,
          <span className="text-red-300"> SUPPRESS</span> = off). <strong className="text-slate-400">Mode</strong>:
          <em> Auto</em> follows the recommendation once past the min changed‑races gate; <em>Force ON/OFF</em> locks it regardless.
        </p>
      </div>

      {msg && <p className="text-sm text-emerald-400" data-testid="signal-effects-msg">{msg}</p>}

      <div className="flex items-end gap-3 bg-slate-900/50 border border-slate-700 rounded-lg p-3">
        <div className="text-xs text-slate-400">
          Auto‑flip gate (SUPPRESS when with‑signal hit% falls below before by the margin, over the min sample):
        </div>
        <label className="text-xs text-slate-400">Min changed races
          <input type="number" value={gate.min_sample} onChange={e => setGate({ ...gate, min_sample: e.target.value })}
            className="ml-2 w-24 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100" data-testid="gate-min-sample" />
        </label>
        <label className="text-xs text-slate-400">Hit% margin
          <input type="number" step="0.1" value={gate.hit_margin} onChange={e => setGate({ ...gate, hit_margin: e.target.value })}
            className="ml-2 w-20 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100" data-testid="gate-hit-margin" />
        </label>
        <button onClick={saveGate} className="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded" data-testid="gate-save">Save gate</button>
      </div>

      {rules.map(rule => (
        <div key={rule} className="space-y-2">
          <h3 className="text-sm font-bold text-slate-300">Rule {rule}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700 text-left">
                  <th className="py-2 pr-3">Algo</th><th className="pr-3">Signal</th>
                  <th className="pr-3">Changed races</th><th className="pr-3">Hit% (before→with)</th>
                  <th className="pr-3">ROI% (before→with)</th><th className="pr-3">Recommendation</th>
                  <th className="pr-3">Effective</th><th className="pr-3">Mode</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.filter(r => r.rule_set === rule).map(r => {
                  const hd = delta(r.hit_before, r.hit_with);
                  return (
                    <tr key={r.id} className="border-b border-slate-800" data-testid={`se-row-${r.id}`}>
                      <td className="py-2 pr-3 text-slate-300">{r.algo === 'logarithmic' ? 'Log' : 'Original'}</td>
                      <td className="pr-3 text-slate-300">{r.signal === 'must_use' ? 'Strong' : r.signal === 'overbet' ? 'Overbet' : 'Fade'}</td>
                      <td className="pr-3 text-slate-300">{r.changed_n || (r.changed_n === 0 ? '0 (inert)' : '—')}</td>
                      <td className="pr-3">
                        {r.hit_before == null ? '—' : (
                          <span className={hd >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {r.hit_before}% → {r.hit_with}% ({hd >= 0 ? '+' : ''}{hd?.toFixed(1)})
                          </span>)}
                      </td>
                      <td className="pr-3 text-slate-400">{r.roi_before == null ? '—' : `${r.roi_before}% → ${r.roi_with}%`}</td>
                      <td className="pr-3">{r.recommendation
                        ? <span className={r.recommendation === 'SUPPRESS' ? 'text-red-300' : 'text-emerald-300'}>{r.recommendation}</span>
                        : <span className="text-slate-500">no data</span>}</td>
                      <td className="pr-3"><span className={`px-2 py-0.5 rounded text-xs ${stateBadge(r.effective_state)}`}>{r.effective_state}</span></td>
                      <td className="pr-3">
                        <select value={r.mode} onChange={e => setMode(r.id, e.target.value)}
                          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100 text-xs" data-testid={`se-mode-${r.id}`}>
                          <option value="auto">Auto (learn)</option>
                          <option value="force_on">Force ON</option>
                          <option value="force_off">Force OFF</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
